import { BackendErrorPayload } from '@paywithextend/components/dist/error.utils'
import axios from 'axios'
import { stringify } from 'query-string'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { ConfigResponse } from 'src/models/config.models'
import {
  RemoteConfigIssue,
  RemoteConfigModel,
  RemoteConfigResponse,
} from 'src/models/remoteConfig.models'
import { RootState } from 'src/redux/createRootReducer'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectUnauthedFormApiRequestConfig } from 'src/redux/selectors/axios.selectors'

export const GET_CONFIG_INIT = 'session/remoteConfig/GET_CONFIG_INIT'
export const GET_CONFIG_SUCCESS = 'session/remoteConfig/GET_CONFIG_SUCCESS'
export const GET_CONFIG_FAILURE = 'session/remoteConfig/GET_CONFIG_FAILURE'

export type GetConfigSuccessPayload = ConfigResponse

export type GetConfigFailurePayload = BackendErrorPayload<string>

export interface GetConfigInitAction {
  type: typeof GET_CONFIG_INIT
}
export interface GetConfigSuccessAction {
  type: typeof GET_CONFIG_SUCCESS
  payload: GetConfigSuccessPayload
}
export interface GetConfigFailureAction {
  type: typeof GET_CONFIG_FAILURE
  payload: GetConfigFailurePayload
}

export type GetConfigActions =
  | GetConfigInitAction
  | GetConfigSuccessAction
  | GetConfigFailureAction

const createGetConfigInitAction = (): GetConfigInitAction => ({
  type: GET_CONFIG_INIT,
})
const createGetConfigSuccessAction = (
  payload: GetConfigSuccessPayload,
): GetConfigSuccessAction => ({
  type: GET_CONFIG_SUCCESS,
  payload,
})
const createGetConfigFailureAction = (
  err: GetConfigFailurePayload,
): GetConfigFailureAction => ({
  type: GET_CONFIG_FAILURE,
  payload: err,
})

export const getConfig = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetConfigActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetConfigActions>,
    getState: () => RootState,
  ): Promise<void> => {
    const locale = selectNullSafeLocaleFromState(getState())
    dispatch(createGetConfigInitAction())
    return axios
      .get(
        `/config?${stringify({ locale: locale })}`,
        selectUnauthedFormApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createGetConfigSuccessAction(response.data))
        },
        (err) => {
          dispatch(
            createGetConfigFailureAction(err.response?.data || err.message),
          )
        },
      )
  }
}

export const LOAD_FEATURE_FLAGS_SUCCESS =
  'session/featureFlagger/LOAD_FEATURE_FLAGS_SUCCESS'

export type LoadFeatureFlagsSuccessPayload = Partial<RemoteConfigResponse>

export interface LoadFeatureFlagsSuccessAction {
  type: typeof LOAD_FEATURE_FLAGS_SUCCESS
  payload: LoadFeatureFlagsSuccessPayload
}

export type LoadFeatureFlagsActions = LoadFeatureFlagsSuccessAction

const createLoadFeatureFlagsSuccessAction = (
  payload: LoadFeatureFlagsSuccessPayload,
): LoadFeatureFlagsSuccessAction => ({
  type: LOAD_FEATURE_FLAGS_SUCCESS,
  payload,
})

export const loadFeatureFlags = (cookies: Partial<RemoteConfigResponse>) => {
  return (
    dispatch: ThunkDispatch<RootState, void, LoadFeatureFlagsActions>,
  ): void => {
    dispatch(createLoadFeatureFlagsSuccessAction(cookies))
  }
}

export const UPDATE_FEATURE_FLAG_SUCCESS =
  'session/featureFlagger/UPDATE_FEATURE_FLAG_SUCCESS'

export type UpdateFeatureFlagSuccessPayload = {
  featureFlag: keyof RemoteConfigResponse
  val: boolean
}

export interface UpdateFeatureFlagSuccessAction {
  type: typeof UPDATE_FEATURE_FLAG_SUCCESS
  payload: UpdateFeatureFlagSuccessPayload
}

export type UpdateFeatureFlagActions = UpdateFeatureFlagSuccessAction

const createUpdateFeatureFlagSuccessAction = (
  payload: UpdateFeatureFlagSuccessPayload,
): UpdateFeatureFlagSuccessAction => ({
  type: UPDATE_FEATURE_FLAG_SUCCESS,
  payload,
})

export const updateFeatureFlag = (
  featureFlag: keyof RemoteConfigResponse,
  val: boolean,
) => {
  return (
    dispatch: ThunkDispatch<RootState, void, UpdateFeatureFlagActions>,
  ): void => {
    dispatch(
      createUpdateFeatureFlagSuccessAction({
        featureFlag,
        val,
      }),
    )
  }
}

export const GET_REMOTE_CONFIG_INIT =
  'session/remoteConfig/GET_REMOTE_CONFIG_INIT'
export const GET_REMOTE_CONFIG_SUCCESS =
  'session/remoteConfig/GET_REMOTE_CONFIG_SUCCESS'

export type GetRemoteConfigSuccessPayload = RemoteConfigModel
export interface GetRemoteConfigInitAction {
  type: typeof GET_REMOTE_CONFIG_INIT
}
export interface GetRemoteConfigSuccessAction {
  type: typeof GET_REMOTE_CONFIG_SUCCESS
  payload: GetRemoteConfigSuccessPayload
}

export type GetRemoteConfigActions =
  | GetRemoteConfigInitAction
  | GetRemoteConfigSuccessAction

const createGetRemoteConfigInitAction = (): GetRemoteConfigInitAction => ({
  type: GET_REMOTE_CONFIG_INIT,
})

export const createGetRemoteConfigSuccessAction = (
  payload: GetRemoteConfigSuccessPayload,
): GetRemoteConfigSuccessAction => ({
  type: GET_REMOTE_CONFIG_SUCCESS,
  payload,
})

export const getFirebaseRemoteConfig = (
  remoteConfig: RemoteConfigModel,
  cookies?: Partial<RemoteConfigResponse>,
  configIssue?: SetRemoteConfigIssuePayload,
) => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetRemoteConfigActions | SetRemoteConfigIssueAction
    >,
  ): void => {
    dispatch(createGetRemoteConfigInitAction())
    dispatch(createGetRemoteConfigSuccessAction(remoteConfig))
    dispatch(loadFeatureFlags(cookies || remoteConfig))
    dispatch(
      createSetRemoteConfigIssueAction(configIssue ?? RemoteConfigIssue.None),
    )
  }
}

export const SET_REMOTE_CONFIG_ISSUE =
  'session/remoteConfig/SET_REMOTE_CONFIG_ISSUE'

export type SetRemoteConfigIssuePayload = RemoteConfigIssue
export interface SetRemoteConfigIssueAction {
  type: typeof SET_REMOTE_CONFIG_ISSUE
  payload: SetRemoteConfigIssuePayload
}

export type SetRemoteConfigIssueActions = SetRemoteConfigIssueAction

export const createSetRemoteConfigIssueAction = (
  payload: SetRemoteConfigIssuePayload,
): SetRemoteConfigIssueAction => ({
  type: SET_REMOTE_CONFIG_ISSUE,
  payload,
})
